import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { invoiceService } from '../../services/invoice.service';
import { tripService } from '../../services/trip.service';
import { GenerateInvoiceRequest, TripStatus, Trip, CreateMultiTripInvoiceRequest, CreateInvoiceLineItemRequest } from '../../types';

interface GenerateInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const schema = z.object({
  tripIds: z.array(z.number()).min(1, 'At least one trip is required'),
  ratePerKm: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    z.number().positive('Rate per km must be greater than 0')
  ),
  ratePerTon: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    z.number().min(0, 'Rate per ton cannot be negative').optional()
  ),
  adjustmentAmount: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? 0 : Number(value)),
    z.number().optional()
  ),
  adjustmentReason: z.string().optional(),
  taxAmount: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? 0 : Number(value)),
    z.number().min(0, 'Tax amount cannot be negative').optional()
  ),
  invoiceDiscount: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? 0 : Number(value)),
    z.number().min(0, 'Discount cannot be negative').optional()
  ),
  clientName: z.string().min(1, 'Client name is required'),
  clientReference: z.string().optional(),
  notes: z.string().optional(),
});

type FormInput = z.infer<typeof schema>;

export const GenerateInvoiceModal: React.FC<GenerateInvoiceModalProps> = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const [selectedTripIds, setSelectedTripIds] = useState<number[]>([]);
  const [selectedTrips, setSelectedTrips] = useState<Trip[]>([]);

  const { data: completedTrips } = useQuery({
    queryKey: ['trips', 'status', TripStatus.COMPLETED],
    queryFn: () => tripService.getByStatus(TripStatus.COMPLETED),
    enabled: isOpen,
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      ratePerKm: 2000,
      adjustmentAmount: 0,
      taxAmount: 0,
    },
  });

  useEffect(() => {
    if (!isOpen) {
      reset({
        tripIds: [],
        ratePerKm: 2000,
        adjustmentAmount: 0,
        taxAmount: 0,
      });
      setSelectedTripIds([]);
      setSelectedTrips([]);
    }
  }, [isOpen, reset]);

  useEffect(() => {
    if (completedTrips && selectedTripIds.length > 0) {
      const trips = completedTrips.filter(t => selectedTripIds.includes(t.id));
      setSelectedTrips(trips);
    }
  }, [selectedTripIds, completedTrips]);

  const singleTripMutation = useMutation({
    mutationFn: (data: GenerateInvoiceRequest) => invoiceService.generate(data),
    onSuccess: async () => {
      toast.success('Invoice generated');
      await queryClient.invalidateQueries({ queryKey: ['invoices'] });
      onClose();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to generate invoice');
    },
  });

  const multiTripMutation = useMutation({
    mutationFn: (data: CreateMultiTripInvoiceRequest) => invoiceService.createMultiTrip(data),
    onSuccess: async () => {
      toast.success('Multi-trip invoice created');
      await queryClient.invalidateQueries({ queryKey: ['invoices'] });
      onClose();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to create invoice');
    },
  });

  const onSubmit = (data: FormInput) => {
    const parsed = schema.parse(data);
    
    if (parsed.tripIds.length === 1) {
      singleTripMutation.mutate({
        tripId: parsed.tripIds[0],
        ratePerKm: parsed.ratePerKm,
        adjustmentAmount: parsed.adjustmentAmount ?? 0,
        adjustmentReason: parsed.adjustmentReason?.trim() ? parsed.adjustmentReason : undefined,
        taxAmount: parsed.taxAmount ?? 0,
        clientName: parsed.clientName,
        clientReference: parsed.clientReference?.trim() ? parsed.clientReference : undefined,
        notes: parsed.notes?.trim() ? parsed.notes : undefined,
      });
    } else {
      const lineItems: CreateInvoiceLineItemRequest[] = selectedTrips.map(trip => ({
        tripId: trip.id,
        description: `Trip ${trip.tripNumber}: ${trip.origin} → ${trip.destination}`,
        weightTons: trip.weightInTons || 0,
        distanceKm: trip.actualDistanceKm || trip.plannedDistanceKm,
        ratePerTon: parsed.ratePerTon || 0,
        ratePerKm: parsed.ratePerKm,
        fuelCost: 0,
        allowanceCost: 0,
        otherCharges: 0,
        discountPercentage: 0,
        discountAmount: 0,
      }));

      multiTripMutation.mutate({
        invoiceDate: new Date().toISOString().split('T')[0],
        clientName: parsed.clientName,
        clientReference: parsed.clientReference?.trim() ? parsed.clientReference : undefined,
        lineItems,
        invoiceDiscount: parsed.invoiceDiscount ?? 0,
        adjustmentAmount: parsed.adjustmentAmount ?? 0,
        adjustmentReason: parsed.adjustmentReason?.trim() ? parsed.adjustmentReason : undefined,
        taxAmount: parsed.taxAmount ?? 0,
        notes: parsed.notes?.trim() ? parsed.notes : undefined,
      });
    }
  };

  const handleTripSelection = (tripId: number) => {
    setSelectedTripIds(prev => {
      if (prev.includes(tripId)) {
        return prev.filter(id => id !== tripId);
      } else {
        return [...prev, tripId];
      }
    });
  };

  const totalAmount = selectedTrips.reduce((sum, trip) => {
    const ratePerTon = watch('ratePerTon') as number || 0;
    const ratePerKm = watch('ratePerKm') as number || 0;
    const weightCost = (trip.weightInTons || 0) * ratePerTon;
    const distanceCost = (trip.actualDistanceKm || trip.plannedDistanceKm) * ratePerKm;
    return sum + weightCost + distanceCost;
  }, 0);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Generate Invoice"
      size="xl"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={singleTripMutation.isPending || multiTripMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit(onSubmit)} loading={singleTripMutation.isPending || multiTripMutation.isPending}>
            {selectedTripIds.length > 1 ? 'Create Multi-Trip Invoice' : 'Generate Invoice'}
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Trip(s) <span className="text-red-500">*</span>
          </label>
          <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-lg p-2 space-y-2">
            {(completedTrips || []).map((trip) => (
              <label key={trip.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedTripIds.includes(trip.id)}
                  onChange={() => handleTripSelection(trip.id)}
                  className="rounded border-gray-300"
                />
                <div className="flex-1">
                  <div className="font-medium">{trip.tripNumber}</div>
                  <div className="text-sm text-gray-600">
                    {trip.origin} → {trip.destination} • {trip.actualDistanceKm || trip.plannedDistanceKm} km • {trip.weightInTons || 0} tons
                  </div>
                </div>
              </label>
            ))}
          </div>
          {selectedTripIds.length > 0 && (
            <p className="text-sm text-gray-600 mt-2">
              {selectedTripIds.length} trip(s) selected
            </p>
          )}
          {errors.tripIds && (
            <p className="text-red-500 text-sm mt-1">{errors.tripIds.message}</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Rate per Km"
            required
            type="number"
            step="0.01"
            error={errors.ratePerKm?.message}
            {...register('ratePerKm')}
          />
          {selectedTripIds.length > 1 && (
            <Input
              label="Rate per Ton"
              type="number"
              step="0.01"
              error={errors.ratePerTon?.message}
              {...register('ratePerTon')}
            />
          )}
          <Input
            label="Tax Amount"
            type="number"
            step="0.01"
            error={errors.taxAmount?.message}
            {...register('taxAmount')}
          />
          <Input
            label="Adjustment Amount"
            type="number"
            step="0.01"
            error={errors.adjustmentAmount?.message}
            {...register('adjustmentAmount')}
          />
          <Input
            label="Adjustment Reason"
            placeholder="Optional"
            error={errors.adjustmentReason?.message}
            {...register('adjustmentReason')}
          />
          {selectedTripIds.length > 1 && (
            <Input
              label="Invoice Discount"
              type="number"
              step="0.01"
              error={errors.invoiceDiscount?.message}
              {...register('invoiceDiscount')}
            />
          )}
        </div>

        {selectedTripIds.length > 1 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-2">Multi-Trip Invoice Summary</h3>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span>Total Trips:</span>
                <span className="font-medium">{selectedTripIds.length}</span>
              </div>
              <div className="flex justify-between">
                <span>Total Distance:</span>
                <span className="font-medium">{selectedTrips.reduce((sum, t) => sum + (t.actualDistanceKm || t.plannedDistanceKm), 0)} km</span>
              </div>
              <div className="flex justify-between">
                <span>Total Weight:</span>
                <span className="font-medium">{selectedTrips.reduce((sum, t) => sum + (t.weightInTons || 0), 0)} tons</span>
              </div>
              <div className="flex justify-between border-t border-blue-300 pt-2 mt-2">
                <span className="font-semibold">Estimated Total:</span>
                <span className="font-semibold">
                  {new Intl.NumberFormat('en-TZ', { style: 'currency', currency: 'TZS', minimumFractionDigits: 0 }).format(totalAmount)}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Client Name"
            required
            placeholder="e.g., ABC Company Ltd"
            error={errors.clientName?.message}
            {...register('clientName')}
          />
          <Input
            label="Client Reference"
            placeholder="e.g., PO-2024-001"
            error={errors.clientReference?.message}
            {...register('clientReference')}
          />
        </div>

        <Input
          label="Notes"
          placeholder="Optional"
          error={errors.notes?.message}
          {...register('notes')}
        />

        <button type="submit" className="hidden" />
      </form>
    </Modal>
  );
};
